import { Injectable, Logger, OnModuleDestroy } from '@nestjs/common';
import type { Browser } from 'playwright-core';

/**
 * How many invoices may render at once.
 *
 * Each in-flight render is a browser context holding 40–80 MB. On a single VPS
 * that also runs Postgres and the API under PM2, a burst of downloads spawning
 * an unbounded number of them is the thing that takes the box down — so the
 * queue is the point, not an optimisation.
 */
const MAX_CONCURRENT = Number(process.env.INVOICE_RENDER_CONCURRENCY ?? 2);

/** A slow render is a bug; without a cap it becomes a stuck request forever. */
const RENDER_TIMEOUT_MS = Number(process.env.INVOICE_RENDER_TIMEOUT_MS ?? 20_000);

/**
 * Turns invoice HTML into a PDF, using one long-lived Chromium.
 *
 * `chromium.launch()` per request costs 300–800 ms and 150–250 MB each; doing
 * it per invoice is the single worst thing this service could do on the target
 * hardware. One browser is launched on first use and reused; each render gets
 * its own *context* for isolation and closes it in `finally`.
 *
 * Chromium is not a dependency of issuing an invoice — only of printing one.
 * If it cannot start, `InvoiceService` has already recorded the document; this
 * throws, the caller reports it, and a later request retries. The legal record
 * never depends on a browser.
 */
@Injectable()
export class InvoiceRendererService implements OnModuleDestroy {
  private readonly logger = new Logger(InvoiceRendererService.name);

  private browser: Browser | null = null;
  private launching: Promise<Browser> | null = null;
  private active = 0;
  private readonly waiting: (() => void)[] = [];

  async onModuleDestroy(): Promise<void> {
    await this.browser?.close().catch(() => undefined);
    this.browser = null;
  }

  async render(html: string): Promise<Buffer> {
    await this.acquire();

    try {
      const browser = await this.ensureBrowser();
      const context = await browser.newContext();

      try {
        const page = await context.newPage();

        // 'load', not 'networkidle'. The template inlines everything it needs,
        // so there is no network to go idle — networkidle would simply wait out
        // its 500 ms timer on every invoice, and flake when it did not.
        await page.setContent(html, { waitUntil: 'load', timeout: RENDER_TIMEOUT_MS });

        // Without this the page renders under screen media and any print-only
        // rule in the template is ignored.
        await page.emulateMedia({ media: 'print' });

        return await page.pdf({
          format: 'A4',
          printBackground: true,
          margin: { top: '14mm', right: '12mm', bottom: '14mm', left: '12mm' },
        });
      } finally {
        await context.close().catch(() => undefined);
      }
    } catch (error) {
      // A crashed browser must not poison every later render: drop the handle
      // so the next call launches a fresh one.
      if (!this.browser?.isConnected()) this.browser = null;
      this.logger.error(`invoice render failed: ${(error as Error).message}`);
      throw error;
    } finally {
      this.release();
    }
  }

  /** Whether a PDF can be produced at all — used by health reporting. */
  async available(): Promise<boolean> {
    try {
      await this.ensureBrowser();
      return true;
    } catch {
      return false;
    }
  }

  private async ensureBrowser(): Promise<Browser> {
    if (this.browser?.isConnected()) return this.browser;

    // Concurrent first-callers must not each launch a browser.
    if (!this.launching) {
      this.launching = this.launch().finally(() => {
        this.launching = null;
      });
    }

    return this.launching;
  }

  private async launch(): Promise<Browser> {
    // Imported lazily so an API that never prints an invoice does not pay for
    // loading the driver, and so a missing browser binary surfaces here rather
    // than at module load.
    const { chromium } = await import('playwright-core');

    // A missing browser binary is the single most likely deployment failure —
    // playwright-core ships no browsers, so `playwright install chromium` has
    // to be part of the deploy. Say that in the message rather than letting the
    // driver's own wording ("Executable doesn't exist at ...") reach a log
    // nobody connects back to a deploy step.
    const browser = await chromium.launch({
      headless: true,
      // --no-sandbox is required in most containers and on a bare VPS running
      // as a non-root user without user namespaces. The only thing this browser
      // ever loads is HTML we generated ourselves.
      args: ['--no-sandbox', '--disable-dev-shm-usage'],
      executablePath: process.env.INVOICE_CHROMIUM_PATH || undefined,
    }).catch((error: Error) => {
      // Names the command that actually works on a production box. `npx
      // playwright install` — which the driver's own banner suggests — fetches
      // whatever version the registry serves, because the `playwright` package
      // is not installed there (`npm ci --omit=dev`); that version's browser
      // build need not be the one playwright-core looks for. Its own CLI
      // always installs the matching build.
      //
      // Note the binary it wants is the headless SHELL
      // (chromium_headless_shell-NNNN), not full chromium — `install chromium`
      // lays down both, which is why the command below is still just that.
      throw new Error(
        `Could not start Chromium for invoice rendering. Run this as the user ` +
          `PM2 runs as, from the app directory:
` +
          `  node node_modules/playwright-core/cli.js install --with-deps chromium
` +
          `Or set INVOICE_CHROMIUM_PATH to an existing browser. ` +
          `Underlying error: ${error.message}`
      );
    });

    browser.on('disconnected', () => {
      this.browser = null;
      this.logger.warn('invoice renderer browser disconnected — will relaunch on next render');
    });

    this.browser = browser;
    this.logger.log('invoice renderer browser started');
    return browser;
  }

  private acquire(): Promise<void> {
    if (this.active < MAX_CONCURRENT) {
      this.active += 1;
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      this.waiting.push(() => {
        this.active += 1;
        resolve();
      });
    });
  }

  private release(): void {
    this.active -= 1;
    this.waiting.shift()?.();
  }
}
